import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { autoCompleteProduct } from "../../core/product.core";
import { addOrIncrementCartItem, resolveSingleProductFromScan } from "../../core/scanToCart.core";
import { useTranslation } from "../../core/translation.core";
import useCart from "../../hooks/services/useCart.hook";
import { Cart } from "../../types/cart";
import SearchIcon from "../../icons/posicons/search.icon";
import XMarkIcon from "../../icons/xMark.icon";

interface FormData {
  id: string;
  text?: string;
}

export default function SearchProductPanel() {
  const { push, locale, query } = useRouter();
  const pathname = usePathname();
  const dropDownRef = useRef(null);
  const { msg, t } = useTranslation();
  const [delay, setDelay] = useState("");
  const { register, handleSubmit, control, resetField, reset } = useForm<FormData>();
  const [showAutoComplete, setShowAutoComplete] = useState(false);
  const text = useWatch({ control, name: "text" });
  const { addItem, updateItem, getProductWarehouseId } = useCart();
  const queryClient = useQueryClient();

  const { data: products } = useQuery({
    queryFn: () => autoCompleteProduct(text as string),
    queryKey: ["productAutoComplete", delay, locale],
  });

  async function onSubmit(data: FormData) {
    if (!data.text) return;

    if (pathname === "/") {
      const product = await resolveSingleProductFromScan(data.text);
      if (product) {
        const cart = queryClient.getQueryData<Cart>(["PosCart", locale]);
        addOrIncrementCartItem({
          product,
          cart,
          addItem,
          updateItem,
          getProductWarehouseId,
          queryClient,
        });
        resetField("text");
        return;
      }

      push(`?search=${data.text.replace(/\+/g, "%2B")}`);
    } else {
      push(`/products?search=${data.text.replace(/\+/g, "%2B")}`);
    }
    resetField("text");
  }

  function onClick(text: any) {
    if (pathname === "/") {
      push(`?search=${text.replace(/\+/g, "%2B")}`);
    } else {
      push(`/products?search=${text.replace(/\+/g, "%2B")}`);
    }

    resetField("text");
  }

  useEffect(() => {
    //  @ts-ignore
    if (text?.length > 2) {
      setShowAutoComplete(true);
    } else {
      setShowAutoComplete(false);
    }
  }, [text]);

  //   setting changed after 100ms reduces refectch between keystroke
  useEffect(() => {
    let timer: any;
    //  @ts-ignore
    if (text?.length >= 3) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        //  @ts-ignore
        setDelay(text);
      }, 100);
    }

    return () => clearTimeout(timer);
  }, [text]);

  // close dropDown on click outside
  useEffect(() => {
    const handleToggle = (e) => {
      //  @ts-ignore
      if (!dropDownRef.current.contains(e.target)) reset({ text: "" });
    };
    window.addEventListener("click", handleToggle);
    return () => window.removeEventListener("click", handleToggle);
  }, [reset]);

  const searchQuery = query.search;

  const handleClearSearch = () => {
    reset({ text: "" });
    push(pathname);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="relative flex items-center gap-2 bg-her-lightgray dark:bg-her-backblue dark:text-white w-[400px] md:w-[450px] xl:w-[550px] py-3 px-3 lg:p-3 rounded-lg"
      ref={dropDownRef}
    >
      <SearchIcon />

      <input
        type="text"
        {...register("text")}
        className="outline-none bg-transparent opacity-90 text-xs lg:text-sm w-full"
        placeholder={t(msg.posMenuItems.searchProducts)}
      />

      {searchQuery && (
        <button
          onClick={handleClearSearch}
          type="button"
          className="text-sm px-2 py-1 rounded-md [&>*]:text-gray-500 [&>*]:hover:text-white dark:text-white"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
      <div
        className={`${
          showAutoComplete ? "block" : "hidden"
        } absolute w-[95%] top-12 shadow-lg bg-white dark:bg-her-backblue dark text-sm max-h-[50vh] overflow-y-scroll z-20`}
      >
        <article>
          {products?.list.length === 0 && <p className="px-3 py-2">{t(msg.posMenuItems.searchNoFound)}</p>}

          <ul>
            {products?.list.map((p) => (
              <li
                key={p.id}
                className="flex flex-col  gap-1 border-b truncate border-primary-light border-opacity-20 p-3 hover:bg-opacity-50 cursor-pointer hover:bg-her-lightgray hover:text-white dark:hover:bg-her-darkblue2"
                onClick={() => onClick(p?.name)}
              >
                {p.name}
              </li>
            ))}
          </ul>
        </article>
      </div>
    </form>
  );
}
